import {useCallback, useLayoutEffect, useMemo, useState} from 'react';

import type {ShoppingItem as UdemyShoppingItem} from '@udemy/shopping';

interface CandidateState {
    isLoading: boolean;
    shoppingItems?: UdemyShoppingItem[];
}

export interface SelectedUpsell {
    kind: 'course' | 'voucher';
    id: number;
    shoppingItems: UdemyShoppingItem[];
}

export interface UseUpsellSelectionParams {
    cartCourseItems: UdemyShoppingItem[];
    cartVoucherItems: UdemyShoppingItem[];
}

export interface UseUpsellSelectionResult {
    /** The single upsell row chosen by the cascade, or `null` when none qualify. */
    selected: SelectedUpsell | null;
    /** `true` while the active path still has unsettled candidates. Hide the section while pending. */
    isPending: boolean;
    isSelected: (kind: SelectedUpsell['kind'], id: number) => boolean;
    /** Records the candidate's loading flag — fed by the row's `onLoadingChange`. */
    setLoading: (kind: SelectedUpsell['kind'], id: number, isLoading: boolean) => void;
    /** Records the candidate's resolved content — fed by the row's `onHasContent`. */
    setContent: (
        kind: SelectedUpsell['kind'],
        id: number,
        hasContent: boolean,
        shoppingItems?: UdemyShoppingItem[],
    ) => void;
}

type Selection = {selected: SelectedUpsell; isPending: false} | {selected: null; isPending: true};

function areShoppingItemsEqual(
    a: UdemyShoppingItem[] | undefined,
    b: UdemyShoppingItem[] | undefined,
): boolean {
    if (a === b) return true;
    if (!a || !b || a.length !== b.length) return false;
    return a.every(
        (item, index) =>
            item.buyable.id === b[index].buyable.id &&
            item.buyable.buyable_object_type === b[index].buyable.buyable_object_type,
    );
}

function pickUpsell(
    kind: SelectedUpsell['kind'],
    cartItems: UdemyShoppingItem[],
    candidates: Record<number, CandidateState>,
): Selection | null {
    for (const item of cartItems) {
        const candidate = candidates[item.buyable.id];
        if (!candidate || candidate.isLoading) {
            return {selected: null, isPending: true};
        }
        if (candidate.shoppingItems?.length) {
            return {
                selected: {
                    kind,
                    id: item.buyable.id,
                    shoppingItems: candidate.shoppingItems,
                },
                isPending: false,
            };
        }
    }
    return null;
}

/**
 * Picks at most one upsell to render: first cart voucher with renderable
 * content, else first cart course with renderable content, else nothing.
 * The voucher path must fully settle before the course path is considered, so
 * the section doesn't flicker. Tie-break is cart order — sort the inputs
 * upstream when the All-Vouchers ranking lands.
 */
export function useUpsellSelection({
    cartCourseItems,
    cartVoucherItems,
}: UseUpsellSelectionParams): UseUpsellSelectionResult {
    const [courseCandidates, setCourseCandidates] = useState<Record<number, CandidateState>>({});
    const [voucherCandidates, setVoucherCandidates] = useState<Record<number, CandidateState>>({});

    const cartCourseIdsKey = useMemo(
        () => cartCourseItems.map((item) => item.buyable.id).join(','),
        [cartCourseItems],
    );
    const cartVoucherIdsKey = useMemo(
        () => cartVoucherItems.map((item) => item.buyable.id).join(','),
        [cartVoucherItems],
    );

    useLayoutEffect(
        function seedPendingCandidates() {
            const courseIds = cartCourseIdsKey ? cartCourseIdsKey.split(',').map(Number) : [];
            setCourseCandidates((prev) => {
                const next: Record<number, CandidateState> = {};
                let changed = false;
                for (const id of courseIds) {
                    next[id] = prev[id] ?? {isLoading: true};
                    if (!prev[id]) changed = true;
                }
                if (Object.keys(prev).length !== courseIds.length) changed = true;
                return changed ? next : prev;
            });
        },
        [cartCourseIdsKey],
    );

    useLayoutEffect(
        function seedPendingVoucherCandidates() {
            const voucherIds = cartVoucherIdsKey ? cartVoucherIdsKey.split(',').map(Number) : [];
            setVoucherCandidates((prev) => {
                const next: Record<number, CandidateState> = {};
                let changed = false;
                for (const id of voucherIds) {
                    next[id] = prev[id] ?? {isLoading: true};
                    if (!prev[id]) changed = true;
                }
                if (Object.keys(prev).length !== voucherIds.length) changed = true;
                return changed ? next : prev;
            });
        },
        [cartVoucherIdsKey],
    );

    const setLoading = useCallback(
        (kind: SelectedUpsell['kind'], id: number, isLoading: boolean) => {
            const setCandidates = kind === 'course' ? setCourseCandidates : setVoucherCandidates;
            setCandidates((prev) => {
                const existing = prev[id];
                if (existing?.isLoading === isLoading) return prev;
                return {...prev, [id]: {isLoading, shoppingItems: existing?.shoppingItems}};
            });
        },
        [],
    );

    const setContent = useCallback(
        (
            kind: SelectedUpsell['kind'],
            id: number,
            hasContent: boolean,
            shoppingItems?: UdemyShoppingItem[],
        ) => {
            const nextItems = hasContent && shoppingItems?.length ? shoppingItems : undefined;
            const setCandidates = kind === 'course' ? setCourseCandidates : setVoucherCandidates;
            setCandidates((prev) => {
                const existing = prev[id];
                const nextIsLoading = nextItems ? false : existing?.isLoading ?? false;
                if (
                    existing &&
                    existing.isLoading === nextIsLoading &&
                    areShoppingItemsEqual(existing.shoppingItems, nextItems)
                ) {
                    return prev;
                }
                return {
                    ...prev,
                    [id]: {
                        isLoading: nextIsLoading,
                        shoppingItems: nextItems,
                    },
                };
            });
        },
        [],
    );

    const {selected, isPending} = useMemo<{
        selected: SelectedUpsell | null;
        isPending: boolean;
    }>(
        () =>
            pickUpsell('voucher', cartVoucherItems, voucherCandidates) ??
            pickUpsell('course', cartCourseItems, courseCandidates) ?? {
                selected: null,
                isPending: false,
            },
        [cartCourseItems, cartVoucherItems, courseCandidates, voucherCandidates],
    );

    const isSelected = useCallback(
        (kind: SelectedUpsell['kind'], id: number) => selected?.kind === kind && selected.id === id,
        [selected],
    );

    return {
        selected,
        isPending,
        isSelected,
        setLoading,
        setContent,
    };
}
